import { AxiosError } from 'axios';
import { isEmpty, isEqual, isPlainObject } from 'lodash';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { reverse } from 'named-urls';

import API from 'utils/api';
import Logger from 'utils/logger';
import { isNotFound } from 'utils/api/errors';
import { AuditLinkEntity, AuditRecordDetailsType, EntityValueType } from 'utils/api/modules/entities/entities.types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { EventType } from 'utils/events/constants';
import { ActionEvent } from 'utils/events/types';

import { EntityModules } from 'core/constants';
import routes from 'core/routes';

import AuditQuestionActivities from 'stores/AuditQuestionActivities';
import EntitiesActivities from 'stores/EntitiesActivities';
import EntityDetails from 'stores/EntityDetails/EntityDetails';

import { bindAuditDraft } from 'components/Audits/auditDraft';

import RootStore from '../Root';

const isBlank = (value: unknown): boolean =>
  value == null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && isEmpty(value));

// An unset answer and an emptied one are the same answer, and objects (a location) compare by value.
const sameAnswer = (saved: unknown, next: unknown): boolean =>
  (isBlank(saved) && isBlank(next)) || isEqual(saved, next);

export default class AuditRecordStore extends EntityDetails<AuditRecordDetailsType> {
  private dirtyFields = new Set<string>();

  /** M3.2's per-question store, on the submission host. It owns the feed, links and badge counts. */
  @observable questionActivities?: AuditQuestionActivities;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Audits, routes.dashboard.audits.forms.toString(), (uuid: string): string =>
      reverse(routes.dashboard.audits.forms.details, { formId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(formId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails<AuditRecordDetailsType>(this.entityType, formId)();
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, formId, this.entityType);
        // Only a submitted audit has a submission host; the stats endpoint 404s without one.
        this.questionActivities = data.submission
          ? new AuditQuestionActivities(this.store, this.api, {
              entityType: this.entityType,
              host: 'submission',
              hostId: formId,
            })
          : undefined;
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e as AxiosError;
      });
      if (!isNotFound(e as AxiosError)) {
        Logger.error(`Invalid load audit record API response. Audit id: ${formId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async bindDraft(formId: string): Promise<string | undefined> {
    return bindAuditDraft(this.api, formId, this.store.notification);
  }

  create = async (): Promise<boolean> => {
    Logger.error('Audit records cannot be created directly');
    return false;
  };

  delete = async (formId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, formId)();
    } catch (e) {
      Logger.error('Failed to delete audit', e);
      return false;
    }
    // The caller navigates away next, and a dirty flag would block leaving the record it just deleted.
    runInAction(() => this.clearEdits());
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity<AuditRecordDetailsType>(
        this.entityType,
        this.details.uuid,
        data
      )();
      runInAction(() => {
        this.details = edited.data;
        this.clearEdits();
      });
      this.store.notification.enqueueSuccessSnackbar('Audit edited successfully');
      return true;
    } catch (e) {
      const error = e as AxiosError<string[]>;
      this.store.notification.enqueueErrorSnackbar(
        error.response?.data?.[0] || 'The audit could not be updated. Something went wrong.'
      );
      Logger.error('Failed to edit audit', e);
      return false;
    }
  };

  // Per field, so putting one back does not hide that another is still edited.
  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    if (sameAnswer(this.details?.entity?.[questionId]?.value, newValue)) {
      this.dirtyFields.delete(questionId);
    } else {
      this.dirtyFields.add(questionId);
    }
    this.isContentEdited = this.dirtyFields.size > 0;
  }

  protected onEditFormsClosed(): void {
    this.clearEdits();
  }

  private clearEdits(): void {
    this.dirtyFields.clear();
    this.isContentEdited = false;
  }

  // Only create needs this: complete and delete happen on the task route, which remounts on return.
  @subscribe(EventType.CreatedTask)
  @action
  createdTask({ payload }: ActionEvent<TaskListResponse>): void {
    const details = this.details;
    const linked = payload?.links?.some(
      (link) => link.entity === AuditLinkEntity && link.instance.uuid === details?.uuid
    );
    if (!details || !linked) {
      return;
    }

    const { total, completed } = details.relatedActions || { total: 0, completed: 0 };
    // A task cannot be created already completed, so only the total moves.
    this.details = { ...details, relatedActions: { total: total + 1, completed } };
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.questionActivities = undefined;
    this.clearEdits();
  }
}
